import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useGlobalHotkeys } from '../../contexts/HotkeysContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Search, 
  Plus,
  Bell,
  ChevronDown,
  User,
  Building2,
  LogOut,
  Check,
  ShoppingCart,
  ArrowUpCircle,
  ArrowDownCircle,
  DollarSign,
  Users,
  FileText,
  Package,
  CheckCircle2,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  Info,
  Sun,
  Moon,
  Monitor,
  Gauge,
  Menu
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials, cn } from '../../lib/utils';
import { dashboardAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';

// Botões de ação primária, sempre visíveis no header (fora do dropdown)
const PRIMARY_ACTIONS = [
  { name: 'Nova Receita', href: '/receivables', icon: ArrowUpCircle, color: 'text-success', modal: 'receivable' },
  { name: 'Nova Despesa', href: '/payables', icon: ArrowDownCircle, color: 'text-destructive', modal: 'payable' },
];

// Demais ações rápidas, agrupadas no dropdown "Outros Lançamentos"
const OTHER_ACTIONS = [
  { name: 'Nova Venda', href: '/sales/new', icon: ShoppingCart, color: 'text-green-600' },
  { name: 'Novo Cliente', href: '/entities?type=customer', icon: Users, color: 'text-blue-600' },
  { name: 'Novo Fornecedor', href: '/entities?type=supplier', icon: Package, color: 'text-orange-600' },
  { name: 'Nova Conta Bancária', href: '/bank-accounts', icon: Building2, color: 'text-purple-600' },
];

const ALERT_ICONS = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};

const ALERT_STYLES = {
  error: { bg: 'bg-red-50', icon: 'text-red-600', title: 'text-red-800' },
  warning: { bg: 'bg-amber-50', icon: 'text-amber-600', title: 'text-amber-800' },
  info: { bg: 'bg-blue-50', icon: 'text-blue-600', title: 'text-blue-800' },
  success: { bg: 'bg-green-50', icon: 'text-green-600', title: 'text-green-800' },
};

export function NewHeader() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { openSearch } = useGlobalHotkeys();
  const { toggleMobileSidebar } = useSidebar();
  const [searchValue, setSearchValue] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [alertsCount, setAlertsCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [currentCompanyId, setCurrentCompanyId] = useState(user?.current_company_id);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await dashboardAPI.getAlerts();
      setAlerts(response.data.alerts || []);
      setAlertsCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await companyAPI.list();
      setCompanies(response.data.companies || []);
      setCurrentCompanyId(response.data.current_company_id);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchCompanies();
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts, fetchCompanies]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      openSearch();
    }
  };

  const handleSwitchCompany = async (companyId) => {
    try {
      const response = await companyAPI.switch(companyId);
      setCurrentCompanyId(companyId);
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      if (response.data.company) {
        updateUser({ 
          current_company_id: companyId,
          company_name: response.data.company.name 
        });
      }
      toast.success(`Empresa alterada para ${response.data.company.name}`);
      window.location.reload();
    } catch (error) {
      toast.error('Erro ao trocar de empresa');
    }
  };

  const currentCompany = companies.find(c => c.id === currentCompanyId);

  // Keyboard shortcut for "+ Novo"
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'n') {
        e.preventDefault();
        // Focus the "+ Novo" dropdown trigger
        document.querySelector('[data-testid="new-action-btn"]')?.click();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header
      data-testid="header"
      className="sticky top-0 z-30 h-16 bg-card border-b border-border px-4 md:px-6 flex items-center justify-between gap-2 md:gap-4 transition-colors duration-200"
    >
      {/* Abre a gaveta do menu no celular. Sem este botao a sidebar off-canvas
          nao tem como ser aberta. 44x44 de area de toque (minimo recomendado). */}
      <button
        type="button"
        onClick={toggleMobileSidebar}
        aria-label="Abrir menu"
        className="md:hidden -ml-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        data-testid="mobile-menu-button"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Search Bar — no celular vira so o icone, para sobrar espaco ao menu */}
      <form onSubmit={handleSearchSubmit} className="flex-1 max-w-xl hidden md:block">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar clientes, vendas, produtos..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onClick={openSearch}
            className="pl-10 pr-4 h-10 bg-muted border-border rounded-lg focus:bg-card focus:border-blue-500 transition-colors"
            data-testid="global-search-input"
          />
        </div>
      </form>

      {/* Busca compacta no celular */}
      <button
        type="button"
        onClick={openSearch}
        aria-label="Buscar"
        className="md:hidden flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        data-testid="mobile-search-button"
      >
        <Search className="h-5 w-5" />
      </button>

      <div className="flex-1 md:hidden" />

      {/* Right Actions */}
      <div className="flex items-center gap-3">
        {/* Primary Actions: Nova Receita / Nova Despesa — sempre visíveis */}
        {PRIMARY_ACTIONS.map((action) => {
          const ActionIcon = action.icon;
          return (
            <Button
              key={action.href}
              variant="outline"
              className="font-medium h-10 px-4 rounded-lg hidden sm:flex"
              /* ?new=1 faz a pagina de destino ja abrir o formulario de lancamento,
                 em vez de so mostrar a listagem */
              onClick={() => navigate(`${action.href}?new=1`)}
              data-testid={`primary-action-${action.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <ActionIcon className={cn("h-4 w-4 mr-1.5", action.color)} />
              {action.name}
            </Button>
          );
        })}

        {/* Outros Lançamentos */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="font-medium h-10 px-4 rounded-lg"
              data-testid="new-action-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Outros Lançamentos
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
            {/* Nova Receita / Nova Despesa também aqui para telas pequenas (sm:hidden esconde os botões fixos) */}
            {[...PRIMARY_ACTIONS, ...OTHER_ACTIONS].map((action, index) => {
              const ActionIcon = action.icon;
              return (
                <Fragment key={action.href}>
                  {index === PRIMARY_ACTIONS.length && (
                    <DropdownMenuSeparator className="sm:hidden" />
                  )}
                  <DropdownMenuItem
                    /* itens com formulario proprio abrem direto via ?new=1 */
                    onClick={() => navigate(action.modal ? `${action.href}?new=1` : action.href)}
                    className={cn("cursor-pointer py-2.5", index < PRIMARY_ACTIONS.length && "sm:hidden")}
                    data-testid={`quick-action-${action.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <ActionIcon className={cn("h-4 w-4 mr-3", action.color)} />
                    <span>{action.name}</span>
                  </DropdownMenuItem>
                </Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="relative h-10 w-10"
              data-testid="notifications-btn"
            >
              <Bell className="h-5 w-5 text-muted-foreground" />
              {alertsCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {alertsCount > 9 ? '9+' : alertsCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-0" sideOffset={8}>
            <div className="flex items-center justify-between p-4 border-b">
              <h4 className="font-semibold text-sm">Notificações</h4>
              {alertsCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {alertsCount} {alertsCount === 1 ? 'alerta' : 'alertas'}
                </Badge>
              )}
            </div>
            <ScrollArea className="max-h-80">
              {alerts.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Tudo certo por aqui!</p>
                  <p className="text-xs">Nenhuma notificação pendente.</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {alerts.map((alert, index) => {
                    const Icon = ALERT_ICONS[alert.type] || Info;
                    const styles = ALERT_STYLES[alert.type] || ALERT_STYLES.info;
                    return (
                      <div
                        key={index}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity",
                          styles.bg
                        )}
                        onClick={() => alert.action && navigate(alert.action)}
                      >
                        <Icon className={cn("h-5 w-5 mt-0.5 flex-shrink-0", styles.icon)} />
                        <div className="flex-1 min-w-0">
                          <p className={cn("font-medium text-sm", styles.title)}>{alert.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{alert.message}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-10 w-10"
              data-testid="theme-toggle"
            >
              {resolvedTheme === 'light' ? (
                <Sun className="h-5 w-5 text-amber-500" />
              ) : (
                <Moon className="h-5 w-5 text-blue-400" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40" sideOffset={8}>
            <DropdownMenuLabel className="text-xs">Tema</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => setTheme('light')}
              className={cn("cursor-pointer", theme === 'light' && 'bg-accent')}
            >
              <Sun className="mr-2 h-4 w-4" />
              Claro
              {theme === 'light' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setTheme('dark')}
              className={cn("cursor-pointer", theme === 'dark' && 'bg-accent')}
            >
              <Moon className="mr-2 h-4 w-4" />
              Escuro
              {theme === 'dark' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setTheme('system')}
              className={cn("cursor-pointer", theme === 'system' && 'bg-accent')}
            >
              <Monitor className="mr-2 h-4 w-4" />
              Sistema
              {theme === 'system' && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User/Company Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="flex items-center gap-2 px-2 h-10"
              data-testid="user-menu-btn"
            >
              <Avatar className="h-8 w-8">
                {user?.avatar_url ? (
                  <AvatarImage src={user.avatar_url} alt={user?.name || 'Usuário'} />
                ) : null}
                <AvatarFallback className="bg-blue-600 text-white text-xs font-semibold">
                  {getInitials(user?.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:flex flex-col items-start text-left">
                <span className="text-sm font-medium text-foreground">
                  {currentCompany?.name || user?.company_name || 'Empresa'}
                </span>
                <span className="text-xs text-muted-foreground">Essentials</span>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" sideOffset={8}>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-1">
                <p className="font-medium text-sm">{user?.name || 'Usuário'}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <User className="mr-2 h-4 w-4" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/companies')}>
              <Building2 className="mr-2 h-4 w-4" />
              Minha empresa
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="header-billing-link">
              <Building2 className="mr-2 h-4 w-4" />
              Plano e cobrança
            </DropdownMenuItem>
            {user?.is_super_admin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/admin')}
                  className="text-[#00FFFF]"
                  data-testid="header-admin-link"
                >
                  <Gauge className="mr-2 h-4 w-4" />
                  Admin SaaS
                </DropdownMenuItem>
              </>
            )}
            
            {/* Company Switcher */}
            {companies.length > 1 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">Trocar empresa</DropdownMenuLabel>
                {companies.map((company) => (
                  <DropdownMenuItem
                    key={company.id}
                    onClick={() => handleSwitchCompany(company.id)}
                    className={cn(
                      "flex items-center justify-between cursor-pointer",
                      currentCompanyId === company.id && "bg-blue-50"
                    )}
                  >
                    <span className="text-sm">{company.name}</span>
                    {currentCompanyId === company.id && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </DropdownMenuItem>
                ))}
              </>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={logout}
              className="text-red-600 focus:text-red-600"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
